/**
 * Estoque por filial (redesign admin, Fase 6): saldo, reservado e disponível de
 * cada produto em uma unidade, com filtros e totais. Fonte:
 * `/api/platform/stock/branches` — hoje só seed demonstrativo, e cada linha diz
 * isso. Sem a rota, a tela declara "aguardando contrato" (e mostra o total
 * consolidado real do catálogo como referência, sem repartir por filial).
 */
import { createElement as h, useState, type ReactElement } from "react";
import { Link } from "react-router-dom";
import { Boxes, Lock, PackageCheck } from "lucide-react";
import { CatalogBoundary } from "../components/CatalogBoundary.js";
import { StatusPanel, errorDetail } from "../components/StatusPanel.js";
import type { CatalogDataset } from "../catalog/dataset.js";
import { availabilityOfProduct } from "../catalog/availability.js";
import {
  availableOf,
  branchTotals,
  filterBranchStock,
  situationOf,
  type BalanceFilter,
  type BranchSituation,
  type BranchStockSnapshot
} from "../catalog/branch-stock.js";
import { formatCount, formatDateTime } from "../format.js";
import { useBranchStock, useSaveBranchBalance } from "../queries.js";
import { Button, Card, EmptyState, Field, PageHeader, StatTile, controlClass } from "../ui/index.js";

const SITUATION_META: Record<BranchSituation, { label: string; tone: string }> = {
  disponivel: { label: "Disponível", tone: "bg-success-soft text-success" },
  poucas: { label: "Poucas unidades", tone: "bg-warning-soft text-warning" },
  "sem-saldo": { label: "Sem saldo", tone: "bg-danger-soft text-danger" }
};

const SOURCE_LABEL: Record<BranchStockSnapshot["source"], string> = {
  demo_seed: "Seed demonstrativo",
  platform: "Saldo da equipe",
  erp: "ERP"
};

export interface EstoqueFiliaisViewProps {
  catalog: CatalogDataset;
  /** `null` = rota de saldo por filial não publicada neste ambiente. */
  snapshot: BranchStockSnapshot | null;
  /** C2: ajuste do saldo pela equipe. Ausente = a tela é só leitura (ex.: SSR/teste). */
  onSaveBalance?: (input: { branchId: string; productId: string; balance: number }) => void;
  saving?: boolean;
  saveError?: string | null;
}

export function EstoqueFiliaisView({ catalog, snapshot, onSaveBalance, saving = false, saveError = null }: EstoqueFiliaisViewProps): ReactElement {
  const [branchId, setBranchId] = useState(snapshot?.branches[0]?.id ?? "");
  const [query, setQuery] = useState("");
  const [balance, setBalance] = useState<BalanceFilter>("todos");
  const [editProductId, setEditProductId] = useState("");
  const [editBalance, setEditBalance] = useState("");
  const header = h(PageHeader, {
    title: "Estoque por filial",
    description: "Saldo, reservado e disponível de cada produto em cada unidade. Disponível = máximo(0, saldo − reservado)."
  });

  if (snapshot === null) {
    // A operação tem um único estoque (o do Bling), sem filiais: este é o estado normal, não pendência.
    const live = catalog.sources.platformCatalog !== "awaiting_contract";
    const total = live ? catalog.products.reduce((sum, p) => sum + availabilityOfProduct(p).consolidated, 0) : null;
    return h(
      "div",
      { className: "page page--estoque-filiais flex flex-col gap-6", "data-page": "estoque-filiais" },
      header,
      total === null
        ? null
        : h(StatTile, {
            label: "Disponível (estoque único)",
            value: formatCount(total),
            hint: "Estoque do Bling menos reservas ativas. A operação não tem filiais.",
            icon: Boxes,
            "data-kpi": "estoque-total"
          }),
      h(
        Card,
        { "data-panel": "filiais" },
        h(StatusPanel, {
          kind: "empty",
          title: "Estoque único, sem filiais",
          detail: "O saldo de cada produto vem do Bling e é o mesmo que a Loja exibe. Veja o saldo por produto em Disponibilidade."
        }),
        h("p", { className: "mt-3 text-sm" }, h(Link, { to: "/disponibilidade", className: "font-semibold text-brand-text" }, "Ver disponibilidade por produto"))
      )
    );
  }

  const rows = filterBranchStock(snapshot, { branchId, query, balance });
  const totals = branchTotals(snapshot.items.filter((item) => item.branchId === branchId));
  return h(
    "div",
    { className: "page page--estoque-filiais flex flex-col gap-6", "data-page": "estoque-filiais", "data-stock-source": snapshot.source },
    header,
    snapshot.source === "demo_seed"
      ? h(
          "p",
          { className: "m-0 rounded-panel border border-warning bg-warning-soft px-4 py-3 text-sm text-ink", "data-stock-note": "demo" },
          h("strong", { className: "font-bold" }, "Como ler: "),
          "estes saldos por filial são um seed demonstrativo; o ERP ainda não publica estoque por unidade. Não use para decisão operacional."
        )
      : null,
    h(
      "div",
      { className: "grid grid-cols-1 gap-4 rounded-panel border border-border bg-surface p-4 shadow-s1 md:grid-cols-3" },
      h(Field, {
        label: "Unidade",
        children: (ids) =>
          h(
            "select",
            {
              id: ids.id,
              className: controlClass,
              value: branchId,
              "data-filter-control": "branch",
              onChange: (e: { target: { value: string } }) => setBranchId(e.target.value)
            },
            snapshot.branches.map((branch) => h("option", { key: branch.id, value: branch.id }, branch.name))
          )
      }),
      h(Field, {
        label: "Buscar por SKU ou nome",
        children: (ids) =>
          h("input", {
            id: ids.id,
            type: "search",
            className: controlClass,
            value: query,
            "data-filter-control": "query",
            onChange: (e: { target: { value: string } }) => setQuery(e.target.value)
          })
      }),
      h(Field, {
        label: "Saldo",
        children: (ids) =>
          h(
            "select",
            {
              id: ids.id,
              className: controlClass,
              value: balance,
              "data-filter-control": "balance",
              onChange: (e: { target: { value: string } }) => setBalance(e.target.value as BalanceFilter)
            },
            h("option", { value: "todos" }, "Todos"),
            h("option", { value: "disponivel" }, "Disponível"),
            h("option", { value: "poucas" }, "Poucas unidades"),
            h("option", { value: "sem-saldo" }, "Sem saldo")
          )
      })
    ),
    h(
      "section",
      { "aria-label": "Totais da unidade", className: "grid grid-cols-1 gap-4 sm:grid-cols-3" },
      h(StatTile, { label: "Saldo total", value: formatCount(totals.balance), icon: Boxes, "data-kpi": "saldo" }),
      h(StatTile, { label: "Reservado", value: formatCount(totals.reserved), icon: Lock, "data-kpi": "reservado" }),
      h(StatTile, { label: "Disponível", value: formatCount(totals.available), icon: PackageCheck, "data-kpi": "disponivel" })
    ),
    rows.length === 0
      ? h(EmptyState, { title: "Nenhum produto neste recorte" })
      : h(
          Card,
          { className: "p-0" },
          h(
            "div",
            { className: "overflow-x-auto" },
            h(
              "table",
              { className: "w-full text-left text-sm", "data-table": "estoque-filial" },
              h(
                "thead",
                null,
                h(
                  "tr",
                  { className: "border-b border-border-strong bg-surface-soft text-xs uppercase tracking-wide text-muted" },
                  ["Produto", "Saldo", "Reservado", "Disponível", "Situação", "Origem e atualização"].map((title, index) =>
                    h("th", { key: title, scope: "col", className: "whitespace-nowrap px-4 py-3 font-bold " + (index >= 1 && index <= 3 ? "text-right" : "") }, title)
                  )
                )
              ),
              h(
                "tbody",
                null,
                rows.map((item) => {
                  const situation = SITUATION_META[situationOf(item)];
                  return h(
                    "tr",
                    { key: `${item.branchId}:${item.productId}`, "data-record-id": `${item.branchId}:${item.productId}`, className: "border-b border-border last:border-b-0 hover:bg-surface-soft" },
                    h(
                      "td",
                      { className: "px-4 py-3" },
                      h("div", { className: "font-bold text-ink" }, item.name),
                      h("div", { className: "text-xs text-muted" }, `SKU ${item.sku}`)
                    ),
                    h("td", { className: "px-4 py-3 text-right tabular-nums" }, formatCount(item.balance)),
                    h("td", { className: "px-4 py-3 text-right tabular-nums" }, formatCount(item.reserved)),
                    h("td", { className: "px-4 py-3 text-right font-extrabold tabular-nums text-ink" }, formatCount(availableOf(item))),
                    h("td", { className: "px-4 py-3" }, h("span", { className: `inline-flex rounded-pill px-2.5 py-0.5 text-xs font-bold ${situation.tone}`, "data-situation": situationOf(item) }, situation.label)),
                    h(
                      "td",
                      { className: "whitespace-nowrap px-4 py-3 text-xs text-muted" },
                      `${SOURCE_LABEL[snapshot.source]} · ${formatDateTime(item.updatedAt)}`
                    )
                  );
                })
              )
            )
          )
        ),
    onSaveBalance
      ? h(
          Card,
          { "data-panel": "ajustar-saldo", className: "flex flex-col gap-3" },
          h("h3", { className: "m-0 text-lg font-extrabold text-ink" }, "Ajustar saldo na unidade"),
          h(
            "p",
            { className: "m-0 text-sm text-muted" },
            "O ERP entrega um saldo único por produto; o saldo por unidade é mantido pela equipe aqui. O reservado não é alterado por este ajuste."
          ),
          h(
            "div",
            { className: "grid grid-cols-1 gap-3 md:grid-cols-[2fr_1fr_auto] md:items-end" },
            h(Field, {
              label: "Produto",
              children: (ids) =>
                h(
                  "select",
                  { id: ids.id, className: controlClass, value: editProductId, "data-edit-control": "product", onChange: (e: { target: { value: string } }) => setEditProductId(e.target.value) },
                  h("option", { value: "" }, "Escolha um produto"),
                  [...new Map(snapshot.items.map((item) => [item.productId, item])).values()].map((item) => h("option", { key: item.productId, value: item.productId }, `${item.name} (${item.sku})`))
                )
            }),
            h(Field, {
              label: "Novo saldo",
              children: (ids) =>
                h("input", { id: ids.id, type: "number", min: 0, step: 1, className: controlClass, value: editBalance, "data-edit-control": "balance", onChange: (e: { target: { value: string } }) => setEditBalance(e.target.value) })
            }),
            h(
              Button,
              {
                variant: "primary",
                disabled: saving || !editProductId || !/^\d+$/.test(editBalance),
                "data-action": "salvar-saldo",
                onClick: () => onSaveBalance({ branchId, productId: editProductId, balance: Number(editBalance) })
              },
              saving ? "Salvando…" : "Salvar saldo"
            )
          ),
          saveError ? h("p", { role: "alert", className: "m-0 text-sm font-semibold text-danger" }, saveError) : null
        )
      : null
  );
}

export function EstoqueFiliais(): ReactElement {
  const save = useSaveBranchBalance();
  const stock = useBranchStock();
  return h(CatalogBoundary, {
    children: (catalog: CatalogDataset) => {
      if (stock.isPending) return h(StatusPanel, { kind: "loading", title: "Carregando saldo por filial…" });
      if (stock.error) return h(StatusPanel, { kind: "error", title: "Não foi possível carregar o saldo por filial", detail: errorDetail(stock.error) });
      return h(EstoqueFiliaisView, {
        catalog,
        snapshot: stock.data ?? null,
        onSaveBalance: (input) => save.mutate(input),
        saving: save.isPending,
        saveError: save.error ? `Não foi possível salvar: ${save.error.message}` : null
      });
    }
  });
}
